import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { utils } from '../../../utils';
import { criarCliente, salvarAreasAtendidas } from '../../../services/clientes';
import { listarAreas } from '../../../services/montagemPalete';
import type { AreaEntrega } from '../../../types/montagemPalete';
import ViewLayout from '../../../components/Common/ViewLayout';

interface ClienteFormData {
  razao_social: string;
  nome_fantasia: string;
  cnpj: string;
  inscricao_estadual: string;
  inscricao_municipal: string;
  email: string;
  telefone: string;
  celular: string;
  contato_nome: string;
  contato_cargo: string;
  endereco_rua: string;
  endereco_numero: string;
  endereco_complemento: string;
  endereco_bairro: string;
  endereco_cidade: string;
  endereco_uf: string;
  endereco_cep: string;
  observacoes: string;
  ativo: boolean;
}

const ClienteCreate: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<ClienteFormData>({
    razao_social: '',
    nome_fantasia: '',
    cnpj: '',
    inscricao_estadual: '',
    inscricao_municipal: '',
    email: '',
    telefone: '',
    celular: '',
    contato_nome: '',
    contato_cargo: '',
    endereco_rua: '',
    endereco_numero: '',
    endereco_complemento: '',
    endereco_bairro: '',
    endereco_cidade: '',
    endereco_uf: '',
    endereco_cep: '',
    observacoes: '',
    ativo: true
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Áreas de entrega atendidas
  const [todasAreas, setTodasAreas] = useState<AreaEntrega[]>([]);
  const [areasAtendidas, setAreasAtendidas] = useState<number[]>([]);

  useEffect(() => {
    listarAreas({ ativo: 'true' }).then(res => {
      if (res.success) setTodasAreas(res.areas);
    }).catch(() => {});
  }, []);

  const toggleAreaAtendida = (areaId: number) => {
    setAreasAtendidas(prev =>
      prev.includes(areaId) ? prev.filter(id => id !== areaId) : [...prev, areaId]
    );
  };

  const selecionarTodasAreas = () => {
    if (areasAtendidas.length === todasAreas.length) {
      setAreasAtendidas([]);
    } else {
      setAreasAtendidas(todasAreas.map(a => a.id));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const formatCNPJ = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 14) {
      return cleaned
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
    }
    return value;
  };

  const formatPhone = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length === 11) {
      return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    } else if (cleaned.length === 10) {
      return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    return value;
  };

  const formatCEP = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 8) {
      return cleaned.replace(/(\d{5})(\d)/, '$1-$2');
    }
    return value;
  };

  const handleCnpjChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, cnpj: formatCNPJ(e.target.value) }));
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: formatPhone(e.target.value) }));
  };

  const handleCepChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, endereco_cep: formatCEP(e.target.value) }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.razao_social.trim()) {
      newErrors.razao_social = 'Razão Social é obrigatória';
    }

    if (!formData.cnpj.trim()) {
      newErrors.cnpj = 'CNPJ é obrigatório';
    } else if (formData.cnpj.replace(/\D/g, '').length !== 14) {
      newErrors.cnpj = 'CNPJ deve ter 14 dígitos';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      utils.showNotification('Por favor, corrija os erros no formulário', { type: 'error' });
      return;
    }

    setIsLoading(true);
    try {
      const response = await criarCliente(formData);

      if (response.success) {
        // Salvar áreas atendidas se alguma foi selecionada
        if (areasAtendidas.length > 0 && response.data?.id) {
          try {
            await salvarAreasAtendidas(response.data.id, areasAtendidas);
          } catch (areaErr) {
            console.error('Erro ao salvar áreas atendidas:', areaErr);
          }
        }
        utils.showNotification('Cliente criado com sucesso!', { type: 'success' });
        navigate('/clientes');
      } else {
        throw new Error(response.message || 'Erro ao criar cliente');
      }
    } catch (error: any) {
      console.error('Erro ao criar cliente:', error);
      utils.showNotification(error.message || 'Erro ao criar cliente', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ViewLayout
      title="Novo Cliente"
      subtitle="Cadastro de Cliente"
      isLoading={isLoading}
      onBack={() => navigate('/clientes')}
    >
      <form onSubmit={handleSubmit}>
        {/* Informações Básicas */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Informações Básicas
          </h3>

          <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <div className="form-group">
              <label className="form-label">Razão Social *</label>
              <input
                type="text"
                name="razao_social"
                value={formData.razao_social}
                onChange={handleInputChange}
                className={`form-control ${errors.razao_social ? 'error' : ''}`}
                placeholder="Razão social da empresa"
              />
              {errors.razao_social && <span className="error-message">{errors.razao_social}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Nome Fantasia</label>
              <input
                type="text"
                name="nome_fantasia"
                value={formData.nome_fantasia}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Nome fantasia"
              />
            </div>

            <div className="form-group">
              <label className="form-label">CNPJ *</label>
              <input
                type="text"
                name="cnpj"
                value={formData.cnpj}
                onChange={handleCnpjChange}
                className={`form-control ${errors.cnpj ? 'error' : ''}`}
                placeholder="00.000.000/0000-00"
                maxLength={18}
              />
              {errors.cnpj && <span className="error-message">{errors.cnpj}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Inscrição Estadual</label>
              <input
                type="text"
                name="inscricao_estadual"
                value={formData.inscricao_estadual}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Inscrição Estadual"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Inscrição Municipal</label>
              <input
                type="text"
                name="inscricao_municipal"
                value={formData.inscricao_municipal}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Inscrição Municipal"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                name="ativo"
                value={formData.ativo ? 'true' : 'false'}
                onChange={(e) => setFormData(prev => ({ ...prev, ativo: e.target.value === 'true' }))}
                className="form-control"
              >
                <option value="true">Ativo</option>
                <option value="false">Inativo</option>
              </select>
            </div>
          </div>
        </div>

        {/* Contato */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Contato
          </h3>

          <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="form-control"
                placeholder="email@empresa.com"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Telefone</label>
              <input
                type="text"
                name="telefone"
                value={formData.telefone}
                onChange={handlePhoneChange}
                className="form-control"
                placeholder="(00) 0000-0000"
                maxLength={15}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Celular</label>
              <input
                type="text"
                name="celular"
                value={formData.celular}
                onChange={handlePhoneChange}
                className="form-control"
                placeholder="(00) 00000-0000"
                maxLength={15}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Nome do Contato</label>
              <input
                type="text"
                name="contato_nome"
                value={formData.contato_nome}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Nome da pessoa de contato"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Cargo do Contato</label>
              <input
                type="text"
                name="contato_cargo"
                value={formData.contato_cargo}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Cargo"
              />
            </div>
          </div>
        </div>

        {/* Endereço */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Endereço
          </h3>

          <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <div className="form-group" style={{ gridColumn: 'span 2' }}>
              <label className="form-label">Rua</label>
              <input
                type="text"
                name="endereco_rua"
                value={formData.endereco_rua}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Nome da rua"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Número</label>
              <input
                type="text"
                name="endereco_numero"
                value={formData.endereco_numero}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Nº"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Complemento</label>
              <input
                type="text"
                name="endereco_complemento"
                value={formData.endereco_complemento}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Sala, andar, etc."
              />
            </div>

            <div className="form-group">
              <label className="form-label">Bairro</label>
              <input
                type="text"
                name="endereco_bairro"
                value={formData.endereco_bairro}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Bairro"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Cidade</label>
              <input
                type="text"
                name="endereco_cidade"
                value={formData.endereco_cidade}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Cidade"
              />
            </div>

            <div className="form-group">
              <label className="form-label">UF</label>
              <select
                name="endereco_uf"
                value={formData.endereco_uf}
                onChange={handleInputChange}
                className="form-control"
              >
                <option value="">Selecione</option>
                {['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'].map(uf => (
                  <option key={uf} value={uf}>{uf}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">CEP</label>
              <input
                type="text"
                name="endereco_cep"
                value={formData.endereco_cep}
                onChange={handleCepChange}
                className="form-control"
                placeholder="00000-000"
                maxLength={9}
              />
            </div>
          </div>
        </div>

        {/* Observações */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Observações
          </h3>

          <div className="form-group">
            <textarea
              name="observacoes"
              value={formData.observacoes}
              onChange={handleInputChange}
              className="form-control"
              rows={4}
              placeholder="Observações sobre o cliente..."
            />
          </div>
        </div>

        {/* Áreas de Entrega Atendidas */}
        {todasAreas.length > 0 && (
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
              <i className="fas fa-map-marker-alt" style={{ marginRight: 8, color: '#e74c3c' }}></i>
              Áreas de Entrega Atendidas
            </h3>
            <p style={{ fontSize: '0.85rem', color: '#7f8c8d', marginBottom: 16 }}>
              Selecione as áreas de entrega que este cliente atende. Se nenhuma área for selecionada, todas as áreas serão exibidas ao montar cargas.
            </p>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#2c3e50' }}>
                {areasAtendidas.length} de {todasAreas.length} áreas selecionadas
              </span>
              <button
                type="button"
                className="btn btn-sm btn-outline"
                onClick={selecionarTodasAreas}
                style={{ fontSize: '0.75rem' }}
              >
                {areasAtendidas.length === todasAreas.length ? 'Desmarcar Todas' : 'Selecionar Todas'}
              </button>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, maxHeight: 300, overflowY: 'auto', padding: 4 }}>
              {todasAreas.map(area => (
                <label
                  key={area.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
                    borderRadius: 8, border: `2px solid ${areasAtendidas.includes(area.id) ? '#04C64F' : '#dce1e6'}`,
                    background: areasAtendidas.includes(area.id) ? '#e8f9ef' : 'white',
                    cursor: 'pointer', fontSize: '0.85rem', transition: 'all 0.2s',
                    userSelect: 'none'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={areasAtendidas.includes(area.id)}
                    onChange={() => toggleAreaAtendida(area.id)}
                    style={{ accentColor: '#04C64F' }}
                  />
                  <span>
                    {area.nome}
                    {area.regiao && <span style={{ color: '#7f8c8d' }}> ({area.regiao})</span>}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/clientes')}
            disabled={isLoading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="loading"></span>
                Salvando...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                Salvar Cliente
              </>
            )}
          </button>
        </div>
      </form>
    </ViewLayout>
  );
};

export default ClienteCreate;
